import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Facebook, Twitter, Instagram, Youtube } from 'lucide-react'
import { useCurrentLanguage } from '@/utils/contentVisibility'
import { getVisibleFooterLinks } from '@/config/routeVisibility'
import { useUiLabels } from '@/hooks/useUiLabels'
import { useLocale } from '@/hooks/useLocale'

export default function Footer() {
  const { t } = useTranslation()
  const { getLabel } = useUiLabels()
  const { localizePath } = useLocale()
  const currentLang = useCurrentLanguage()
  const { important, services } = useMemo(
    () => getVisibleFooterLinks(currentLang),
    [currentLang]
  )

  return (
    <footer className="bg-[#f5f5f5] dark:bg-gray-950 text-gray-700 dark:text-gray-400 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-4">{getLabel('common.about')}</h3>
            <p className="text-sm">
              {getLabel('about.description')}
            </p>
          </div>

          {/* Important Links – safe links only (US-2.F1) */}
          {important.length > 0 && (
            <div>
              <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-4">{getLabel('footer.importantLinks', 'روابط مهمة')}</h3>
              <ul className="space-y-2 text-sm">
                {important.map((item) => (
                  <li key={item.path}>
                    <Link to={localizePath(item.path)} className="hover:text-slate-900 dark:hover:text-white transition-colors">
                      {getLabel(item.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Services */}
          {services.length > 0 && (
            <div>
              <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-4">{getLabel('common.services')}</h3>
              <ul className="space-y-2 text-sm">
                {services.map((item) => (
                  <li key={item.path}>
                    <Link to={localizePath(item.path)} className="hover:text-slate-900 dark:hover:text-white transition-colors">
                      {getLabel(item.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Social Media – always visible (external) */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-4">{getLabel('footer.social', 'مواقع التواصل')}</h3>
            <div className="flex space-x-4 space-x-reverse">
              <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-300 dark:border-gray-800 text-center text-sm">
          <p>{getLabel('footer.copyright').replace('{{year}}', String(new Date().getFullYear())) || `جميع الحقوق محفوظة © ${new Date().getFullYear()} سكينة`}</p>
        </div>
      </div>
    </footer>
  )
}

